import { useRef, useState, type DragEvent, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORY_LABELS,
  DOCUMENT_MAX_BYTES,
  DOCUMENT_OWNER_LABELS,
  DOCUMENT_REFERENCE_LABELS,
  formatBytes,
  type DocumentCategory,
  type DocumentItem,
  type DocumentOwnerKind,
  type DocumentReferenceKind,
  type Paginated,
} from '@kontor/shared';
import { api, apiUpload, ApiError, queryString } from '../../api';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { SortTh } from '../../components/SortTh';
import { useSession } from '../../session';
import { TargetPicker, targetPath, type PickedTarget } from './TargetPicker';
import './documents.css';

const day = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.heic,.docx,.xlsx,.pptx,.odt,.ods,.odp,.txt,.csv,.md';

interface UploadResult {
  name: string;
  ok: boolean;
  message: string;
}

/**
 * Dokumente (KON-163): Dateien zu Kunden und Aufträgen ablegen, finden, ändern und löschen. Beim Hochladen wählt man Art, Kontakt, Auftrag und Notiz, sie gelten für alle
 * Dateien dieses Vorgangs. Der Speicherplatz zählt zum Kontingent des Pakets (Einrichtung → Speicherverwaltung).
 */
export function DocumentsPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const category = params.get('category') ?? '';
  const contactId = params.get('contactId') ?? '';
  const employeeId = params.get('employeeId') ?? '';
  const referenceKind = params.get('referenceKind') ?? '';
  const referenceId = params.get('referenceId') ?? '';
  const sort = params.get('sort') ?? '';
  const page = Number(params.get('page') ?? 1);
  const [editing, setEditing] = useState<DocumentItem | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const list = useQuery({
    queryKey: ['documents', { q, category, contactId, employeeId, referenceKind, referenceId, sort, page }],
    queryFn: () => api<Paginated<DocumentItem>>('GET', `/documents${queryString({ q, category, contactId, employeeId, referenceKind, referenceId, sort, page, perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };
  const fields: FilterField[] = [
    { key: 'category', label: 'Art', type: 'select', options: [{ value: '', label: 'Alle' }, ...DOCUMENT_CATEGORIES.map((c) => ({ value: c, label: DOCUMENT_CATEGORY_LABELS[c] }))] },
  ];
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `/documents/${id}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['documents'] });
      client.invalidateQueries({ queryKey: ['storage'] });
      setMessage({ ok: true, text: 'Das Dokument ist gelöscht.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Dokument konnte nicht gelöscht werden.') }),
  });
  const onSort = (next: string) => update({ sort: next });
  const mayWrite = can('documents.write');
  const mayDelete = can('documents.delete');

  return (
    <>
      <div className="page-head">
        <h1>Dokumente</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} insgesamt` : ''}</span>
      </div>
      {mayWrite && <UploadCard presetContactId={contactId} onDone={() => { client.invalidateQueries({ queryKey: ['documents'] }); client.invalidateQueries({ queryKey: ['storage'] }); }} />}
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      <FilterBar
        search={{ value: q, placeholder: 'Name, Notiz, Kontakt oder Auftragsnummer', onSubmit: (v) => update({ q: v }) }}
        fields={fields}
        values={{ category }}
        onChange={(key, value) => update({ [key]: value })}
        onReset={() => setParams(new URLSearchParams())}
      />
      {(contactId || employeeId || referenceId) && (
        <p className="muted small">
          Gefiltert nach {contactId ? 'einem Kontakt' : employeeId ? 'einem Mitarbeiter' : 'einer Referenz'}. <button type="button" className="link-button" onClick={() => update({ contactId: '', employeeId: '', referenceKind: '', referenceId: '' })}>Filter aufheben</button>
        </p>
      )}
      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}
      {editing && (
        <EditCard
          key={editing.id}
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            client.invalidateQueries({ queryKey: ['documents'] });
          }}
        />
      )}
      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <SortTh label="Dokument" sortKey="name" sort={sort} onSort={onSort} />
              <SortTh label="Art" sortKey="category" sort={sort} onSort={onSort} />
              <th>Gehört zu</th>
              <th>Referenz</th>
              <SortTh label="Größe" sortKey="size" sort={sort} onSort={onSort} first="desc" className="num" />
              <SortTh label="Hochgeladen" sortKey="createdAt" sort={sort} onSort={onSort} first="desc" />
              <th />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={7} className="muted">Wird geladen …</td></tr>}
            {list.data?.data.length === 0 && (
              <tr><td colSpan={7} className="empty">{q || category || contactId || employeeId || referenceId ? 'Keine Dokumente zu dieser Suche.' : 'Noch keine Dokumente. Laden Sie oben die erste Datei hoch.'}</td></tr>
            )}
            {list.data?.data.map((d) => (
              <ExpandRow
                key={d.id}
                summary={
                  <Summary
                    title={<a href={`/api/v1/documents/${d.id}/download`} className="strong-link">{d.title ?? d.fileName}</a>}
                    aside={<span className="doc-kind">{DOCUMENT_CATEGORY_LABELS[d.category]}</span>}
                    line2={[d.owner?.label, d.reference?.label].filter(Boolean).join(' · ')}
                    line3={[formatBytes(d.sizeBytes), day.format(new Date(d.createdAt))].join(' · ')}
                  />
                }
              >
                <td className="cell-desktop-title">
                  <a href={`/api/v1/documents/${d.id}/download`} className="strong-link">{d.title ?? d.fileName}</a>
                  {d.title && <div className="muted small">{d.fileName}</div>}
                  {d.note && <div className="muted small">{d.note}</div>}
                </td>
                <td data-label="Art"><span className="doc-kind">{DOCUMENT_CATEGORY_LABELS[d.category]}</span></td>
                <td data-label="Gehört zu">
                  {d.owner ? (
                    <>
                      <div className="muted small">{DOCUMENT_OWNER_LABELS[d.owner.kind]}</div>
                      {d.owner.label ? <Link to={targetPath(d.owner.kind, d.owner.id)}>{d.owner.label}</Link> : <span className="muted">nicht mehr vorhanden</span>}
                    </>
                  ) : (
                    <span className="muted">–</span>
                  )}
                </td>
                <td data-label="Referenz">
                  {d.reference ? (
                    <>
                      <div className="muted small">{DOCUMENT_REFERENCE_LABELS[d.reference.kind]}</div>
                      {d.reference.label ? <Link to={targetPath(d.reference.kind, d.reference.id)}>{d.reference.label}</Link> : <span className="muted">nicht mehr vorhanden</span>}
                    </>
                  ) : (
                    <span className="muted">–</span>
                  )}
                </td>
                <td data-label="Größe" className="num">{formatBytes(d.sizeBytes)}</td>
                <td data-label="Hochgeladen">{day.format(new Date(d.createdAt))}{d.uploadedByName && <div className="muted small">{d.uploadedByName}</div>}</td>
                <td className="doc-actions">
                  <a className="btn btn--secondary btn--sm" href={`/api/v1/documents/${d.id}/download`} aria-label={`${d.fileName} herunterladen`}>Herunterladen</a>
                  {mayWrite && <button type="button" className="btn btn--secondary btn--sm" onClick={() => setEditing(d)} aria-label={`${d.fileName} bearbeiten`}>Bearbeiten</button>}
                  {mayDelete && (
                    <button type="button" className="btn btn--danger btn--sm" disabled={remove.isPending} aria-label={`${d.fileName} löschen`} onClick={() => window.confirm(`„${d.title ?? d.fileName}“ endgültig löschen?`) && remove.mutate(d.id)}>
                      Löschen
                    </button>
                  )}
                </td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && list.data.meta.lastPage > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </div>
      )}
    </>
  );
}

function UploadCard({ presetContactId, onDone }: { presetContactId: string; onDone: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [category, setCategory] = useState<DocumentCategory>('other');
  const [owner, setOwner] = useState<PickedTarget | null>(presetContactId ? { kind: 'contact', id: presetContactId, label: 'Kontakt aus dem Filter' } : null);
  const [reference, setReference] = useState<PickedTarget | null>(null);
  const [note, setNote] = useState('');
  const [results, setResults] = useState<UploadResult[]>([]);
  const [busy, setBusy] = useState(false);

  async function send(files: FileList | File[]) {
    const list = [...files];
    if (list.length === 0) return;
    setBusy(true);
    const out: UploadResult[] = [];
    for (const file of list) {
      if (file.size > DOCUMENT_MAX_BYTES) {
        out.push({ name: file.name, ok: false, message: `Zu groß, erlaubt sind ${formatBytes(DOCUMENT_MAX_BYTES)}.` });
        continue;
      }
      try {
        const qs = queryString({ category, ownerKind: owner?.kind, ownerId: owner?.id, referenceKind: reference?.kind, referenceId: reference?.id, note: note.trim() });
        await apiUpload<{ data: DocumentItem }>(`/documents${qs}`, new File([file], file.name, { type: 'application/octet-stream' }));
        out.push({ name: file.name, ok: true, message: 'abgelegt' });
      } catch (e) {
        out.push({ name: file.name, ok: false, message: errorText(e, 'Das Hochladen hat nicht geklappt.') });
      }
    }
    setResults(out);
    setBusy(false);
    onDone();
  }
  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    void send(e.dataTransfer.files);
  };

  return (
    <section className="card doc-upload" aria-labelledby="doc-upload-title">
      <h2 id="doc-upload-title">Dokument hochladen</h2>
      <div className="form-grid">
        <label className="field">
          <span>Art</span>
          <select className="input" value={category} onChange={(e) => setCategory(e.target.value as DocumentCategory)}>
            {DOCUMENT_CATEGORIES.map((c) => <option key={c} value={c}>{DOCUMENT_CATEGORY_LABELS[c]}</option>)}
          </select>
        </label>
        <div className="field">
          <span>Gehört zu (optional)</span>
          <TargetPicker role="owner" value={owner} onChange={setOwner} label="Gehört zu" />
        </div>
        <div className="field">
          <span>Referenz (optional)</span>
          <TargetPicker role="reference" value={reference} onChange={setReference} label="Referenz" />
        </div>
        <label className="field field--wide">
          <span>Notiz (optional)</span>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
        </label>
      </div>
      <div className={`import__drop${over ? ' is-over' : ''}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}>
        <p className="import__hint">Dateien hierher ziehen oder</p>
        <button type="button" className="btn btn--primary" disabled={busy} onClick={() => input.current?.click()}>{busy ? 'Wird hochgeladen …' : 'Dateien wählen'}</button>
        <p className="muted small">PDF, Bilder, Word, Excel, PowerPoint, OpenDocument, Text und CSV, je Datei höchstens {formatBytes(DOCUMENT_MAX_BYTES)}</p>
        <input ref={input} type="file" multiple accept={ACCEPT} hidden onChange={(e) => { void send(e.target.files ?? []); e.target.value = ''; }} />
      </div>
      {results.length > 0 && (
        <ul className="doc-results" aria-live="polite">
          {results.map((r, i) => <li key={`${r.name}-${i}`} className={r.ok ? 'is-ok' : 'is-error'}><strong>{r.name}</strong>: {r.message}</li>)}
        </ul>
      )}
    </section>
  );
}

function EditCard({ item, onClose, onSaved }: { item: DocumentItem; onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState(item.title ?? '');
  const [category, setCategory] = useState<DocumentCategory>(item.category);
  const [owner, setOwner] = useState<PickedTarget | null>(item.owner);
  const [reference, setReference] = useState<PickedTarget | null>(item.reference);
  const [note, setNote] = useState(item.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api('PATCH', `/documents/${item.id}`, {
        title: title.trim() || null,
        category,
        owner: owner ? { kind: owner.kind as DocumentOwnerKind, id: owner.id } : null,
        reference: reference ? { kind: reference.kind as DocumentReferenceKind, id: reference.id } : null,
        note: note.trim() || null,
      }),
    onSuccess: onSaved,
    onError: (e) => setError(errorText(e, 'Das Speichern hat nicht geklappt.')),
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    save.mutate();
  }
  return (
    <form className="card doc-edit" onSubmit={submit} aria-label={`${item.fileName} bearbeiten`}>
      <h2>Dokument bearbeiten</h2>
      <p className="muted small">{item.fileName} · {formatBytes(item.sizeBytes)}. Die Datei selbst bleibt unverändert.</p>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <div className="form-grid">
        <label className="field field--wide">
          <span>Titel (optional)</span>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder={item.fileName} />
        </label>
        <label className="field">
          <span>Art</span>
          <select className="input" value={category} onChange={(e) => setCategory(e.target.value as DocumentCategory)}>
            {DOCUMENT_CATEGORIES.map((c) => <option key={c} value={c}>{DOCUMENT_CATEGORY_LABELS[c]}</option>)}
          </select>
        </label>
        <div className="field">
          <span>Gehört zu</span>
          <TargetPicker role="owner" value={owner} onChange={setOwner} label="Gehört zu" />
        </div>
        <div className="field">
          <span>Referenz</span>
          <TargetPicker role="reference" value={reference} onChange={setReference} label="Referenz" />
        </div>
        <label className="field field--wide">
          <span>Notiz</span>
          <textarea className="input textarea textarea--short" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
        </label>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>
        <button type="button" className="btn btn--secondary" onClick={onClose}>Abbrechen</button>
      </div>
    </form>
  );
}
